import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import moment from 'moment-jalaali';
import Form from '../../Components/Boilerplates/Forms/Forms';
import Listview2 from '../../Components/Boilerplates/Listview/Listview2';
import useHttp from '../../hooks/http';

const uploadInputs = [
  {
    id: 'lake_id',
    elementConfig: {
      type: 'text',
      placeholder: 'کد دریاچه',
    },
    value: '',
    validation: {
      required: true,
      isNumeric: true,
    },
    valid: false,
    touched: false,
    queryParams: true,
  },
  {
    id: 'file',
    elementConfig: {
      type: 'file',
      accept: 'csv',
      placeholder: 'Lfile',
    },
    value: '',
    validation: {
      required: true,
    },
    valid: false,
    touched: true,
    single: true,
  },
];

const getInputs = [
  {
    id: 'lake_id',
    elementConfig: {
      type: 'text',
      placeholder: 'کد دریاچه',
    },
    value: '',
    validation: {
      required: true,
      isNumeric: true,
    },
    valid: false,
    touched: false,
    queryParams: true,
  },
  {
    id: 'start_date',
    elementConfig: {
      type: 'calendar',
      placeholder: 'تاریخ شروع',
    },
    value: moment(),
    requestValue: moment().format('YYYY-MM-DD'),
    validation: {
      required: true,
    },
    valid: true,
    touched: true,
    queryParams: true,
  },
  {
    id: 'end_date',
    elementConfig: {
      type: 'calendar',
      placeholder: 'تاریخ پایان',
    },
    value: moment(),
    requestValue: moment().format('YYYY-MM-DD'),
    validation: {
      required: true,
    },
    valid: true,
    touched: true,
    queryParams: true,
  },
];

const manageTypes = {
  Add: {
    type: 'form',
    inputs: {
      level: [
        {
          id: 'lake_id',
          elementConfig: {
            type: 'text',
            placeholder: 'کد دریاچه',
          },
          value: '',
          validation: {
            required: true,
            isNumeric: true,
          },
          valid: false,
          touched: false,
        },
        {
          id: 'date',
          elementConfig: {
            type: 'calendar',
            placeholder: 'تاریخ',
          },
          value: moment(),
          requestValue: moment().format('YYYY-MM-DD'),
          validation: {
            required: true,
          },
          valid: true,
          touched: true,
        },
        {
          id: 'level',
          elementConfig: {
            type: 'text',
            placeholder: 'تراز',
          },
          value: '',
          validation: {
            required: true,
            isNumeric: true,
          },
          valid: false,
          touched: false,
        },
        {
          id: 'total_input',
          elementConfig: {
            type: 'text',
            placeholder: 'مجموع ورودی',
          },
          value: '',
          validation: {
            required: true,
            isNumeric: true,
          },
          valid: false,
          touched: false,
        },
      ],
      evaporation: [
        {
          id: 'lake_id',
          elementConfig: {
            type: 'text',
            placeholder: 'کد دریاچه',
          },
          value: '',
          validation: {
            required: true,
            isNumeric: true,
          },
          valid: false,
          touched: false,
        },
        {
          id: 'date',
          elementConfig: {
            type: 'calendar',
            placeholder: 'تاریخ',
          },
          value: moment(),
          requestValue: moment().format('YYYY-MM-DD'),
          validation: {
            required: true,
          },
          valid: true,
          touched: true,
        },
        {
          id: 'evaporation',
          elementConfig: {
            type: 'text',
            placeholder: 'تبخیر',
          },
          value: '',
          validation: {
            required: true,
            isNumeric: true,
          },
          valid: false,
          touched: false,
        },
      ],
    },
    url: (kind) => 'add/lake_' + kind,
    title: 'افزودن اطلاعات دریاچه',
  },
  Upload: {
    type: 'form',
    inputs: {
      level: uploadInputs,
      evaporation: uploadInputs,
    },
    url: (kind) => 'upload/lake_' + kind,
    title: 'آپلود اطلاعات دریاچه',
  },
  Get: {
    type: 'form',
    inputs: {
      level: getInputs,
      evaporation: getInputs,
    },
    method: 'GET',
    notjson: true,
    handleResponse: (response, setMessage) => {
      if (response.ok) {
        return response
          .blob()
          .then((data) => {
            const the = response.headers.get('Content-disposition');
            const thea = the.slice(the.indexOf('"') + 1, the.length - 1);

            return {
              blob: data,
              fileName: thea,
            };
          })
          .then(({ blob, fileName }) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
            a.click();
            a.remove(); //afterwards we remove the element again)
          })
          .catch((error) => {
            console.log(error);
          });
      } else {
        setMessage({ message: response.statusText, type: 'error' });
      }
    },
    btnText: 'دریافت',
    url: (kind) => `download/admin/lake_${kind}_detail`,
    title: 'دریافت اطلاعات دریاچه',
  },
  List: {
    type: 'list',
    url: 'list_view/lake_detail',
    list2: {
      evaporation: [{ en: 'evaporation', fa: 'تبخیر' }],
      level: [
        { en: 'level', fa: 'تراز' },
        { en: 'volume', fa: 'حجم' },
        { en: 'area', fa: 'سطح' },
        { en: 'total_input', fa: 'ورودی دریاچه' },
      ],
    },
  },
};

const Lake = () => {
  const { type } = useParams();
  const { kind, date } = useLocation().state;
  const thisPageData = useMemo(() => ({ ...manageTypes[type] }), [type]);
  const token = useSelector((state) => state.auth.Token);
  const { isLoading, data, error, sendRequest, clear } = useHttp();
  const [codes, setCodes] = useState();

  useEffect(() => {
    sendRequest('get/lake/', 'GET', null, { Authorization: `Bearer ${token}` });
  }, [sendRequest, token]);

  useEffect(() => {
    if (!isLoading && !error && data) {
      console.log(data);
      const the = [];

      data.forEach((element) => {
        the.push({ value: element.name, gainValue: element.id });
      });

      setCodes({
        elementConfig: {
          type: 'select',
          placeholder: 'کد دریاچه',
          options: the,
        },
        indexValue: -1,
      });
      clear();
    }
  }, [error, isLoading, data, clear, setCodes]);

  const theReturn = useMemo(() => {
    switch (thisPageData.type) {
      case 'list':
        return (
          <Listview2 list2={thisPageData.list2[kind.en]} url2={`${thisPageData.url + date}&lake_item=${kind.en}`} />
        );
      case 'form':
      default:
        const theInputs = [...thisPageData.inputs[kind.en]];
        if (codes) {
          theInputs[0] = { ...theInputs[0], ...codes };
        }
        const theUrl = thisPageData.url(kind.en);

        return (
          <Form
            {...thisPageData}
            inputs={theInputs}
            url={theUrl}
            pageTitle={thisPageData.title ? thisPageData.title + ` (${kind.fa})` : ''}
            header={{ Authorization: `Bearer ${token}` }}
            btnStyle={{
              borderRadius: '25px',
              width: '100px',
              marginLeft: '2rem',
              fontWeight: 'bold',
            }}
          />
        );
    }
  }, [thisPageData, token, kind, date, codes]);

  return theReturn;
};

export default Lake;
